import { useCallback, useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Link2, MessageSquare, QrCode, ShieldCheck, Copy, Gift, Undo2, IndianRupee } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
export const PAY_LABEL = {
  PAYMENT_PENDING: ["Payment Pending", "bg-amber-100 text-amber-900 border-amber-400"],
  AWAITING_VERIFICATION: ["Awaiting Verification", "bg-sky-100 text-sky-900 border-sky-400"],
  PARTIALLY_PAID: ["Partially Paid", "bg-violet-100 text-violet-900 border-violet-400"],
  PAID: ["Paid", "bg-emerald-100 text-emerald-900 border-emerald-500"],
  CANCELLED: ["Cancelled", "bg-stone-200 text-stone-700 border-stone-400"],
};
const err = (e) => e?.response?.data?.detail || e?.message || "Failed";

export function PaymentStatusChip({ st, testid }) {
  if (!st) return null;
  const [l, c] = PAY_LABEL[st.status] || [st.status, ""];
  return <span className={`inline-block rounded-full border px-2 py-0.5 text-xs font-bold ${c}`} data-testid={testid}>{l}</span>;
}

export function usePaymentStatuses(bookingType, ids, token) {
  const [map, setMap] = useState({});
  const key = ids.join(",");
  const reload = useCallback(async () => {
    if (!ids.length || !token) return;
    try { setMap((await api.post("/booking-payments/statuses", { token, booking_type: bookingType, ids })).data.statuses || {}); } catch { /* list still usable */ }
  }, [key, token, bookingType]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, [reload]);
  return [map, reload];
}

export function actionLabel(st) {
  if (!st) return "Payment";
  if (st.awaiting_verification) return "Verify Payment";
  return { PAID: "View Payment", PARTIALLY_PAID: "Request Balance" }[st.status] || "Share Payment Link";
}

const Row = ({ l, v, b }) => <div className="flex justify-between gap-3 py-1 text-sm sm:text-base"><span className="text-[#5a3a22]">{l}</span><span className={`text-right ${b ? "font-bold" : ""}`}>{v}</span></div>;

function VerifyForm({ data, token, onDone }) {
  const [f, setF] = useState({ amount: data.balance, method: "UPI", reference: "", payment_date: new Date().toISOString().slice(0, 10), notes: "", ok: false });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.post("/booking-payments/verify", { token, booking_type: data.booking_type, booking_id: data.booking_id, amount: Number(f.amount),
        method: f.method, reference: f.reference, payment_date: f.payment_date, notes: f.notes, confirmed_official_account: f.ok });
      toast.success("Payment verified and recorded"); onDone(r.data);
    } catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border-2 border-emerald-300 bg-emerald-50/60 p-3 space-y-2" data-testid="bp-verify-form">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div><Label>Amount received</Label><Input type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="bp-verify-amount" /></div>
        <div><Label>Method</Label>
          <Select value={f.method} onValueChange={(v) => setF({ ...f, method: v })}>
            <SelectTrigger data-testid="bp-verify-method"><SelectValue /></SelectTrigger>
            <SelectContent>{["UPI", "BANK", "CARD", "CASH", "OTHER"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><Label>UTR / Txn reference</Label><Input value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} data-testid="bp-verify-utr" /></div>
        <div><Label>Payment date</Label><Input type="date" value={f.payment_date} onChange={(e) => setF({ ...f, payment_date: e.target.value })} data-testid="bp-verify-date" /></div>
      </div>
      <Input placeholder="Notes (optional)" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="bp-verify-notes" />
      <label className="flex items-start gap-2 text-sm"><Checkbox checked={f.ok} onCheckedChange={(v) => setF({ ...f, ok: !!v })} data-testid="bp-verify-confirm" />
        I have checked that this amount is credited to the official {data.centre_name} account.</label>
      <button type="button" className="bi-pill !bg-emerald-700 !text-white" disabled={busy || !f.ok} onClick={save} data-testid="bp-verify-save">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}Mark Payment Received</button>
    </div>
  );
}

export default function BookingPaymentPanel({ session, bookingType, bookingId, onChanged }) {
  const token = session?.token;
  const [d, setD] = useState(null);
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState("");
  const [wa, setWa] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const isAdmin = session?.is_admin || session?.is_super_admin;

  const load = useCallback(async () => {
    try {
      const r = (await api.post("/booking-payments/booking", { token, booking_type: bookingType, booking_id: bookingId })).data;
      setD(r); setAmount((a) => (a === "" ? String(r.balance) : a));
    } catch (e) { toast.error(err(e)); }
  }, [token, bookingType, bookingId]);
  useEffect(() => { load(); }, [load]);
  const changed = (r) => { setWa(null); if (r?.booking_id) { setD(r); setAmount(String(r.balance)); } else { setAmount(""); load(); } onChanged?.(); };

  if (!d) return <div className="p-6 text-center"><Loader2 className="h-6 w-6 animate-spin inline" /></div>;
  const sym = d.currency_symbol;
  const money = (v) => `${sym}${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const gv = d.booking_type === "GIFT_VOUCHER";
  const openReq = (d.requests || []).find((r) => ["REQUESTED", "SHARED"].includes(r.status));
  const canRequest = !d.centre_closed && d.status !== "PAID" && d.status !== "CANCELLED" && d.payment.mode !== "NOT_CONFIGURED";

  const generate = async () => {
    setBusy("gen"); setWa(null);
    try {
      const r = (await api.post("/booking-payments/generate", { token, booking_type: d.booking_type, booking_id: d.booking_id, amount: Number(amount),
        purpose: Number(amount) >= d.balance ? (d.paid > 0 ? "Balance" : "Full Payment") : "Advance" })).data;
      toast.success(r.request.mode === "INDIA_UPI" ? `Payment link ready for ${money(r.request.amount_requested)}` : "Direct-centre payment request saved");
      await share(r.request.id); onChanged?.(); setAmount(""); load();
    } catch (e) { toast.error(err(e)); } finally { setBusy(""); }
  };
  const share = async (rid) => {
    const r = (await api.post("/booking-payments/compose-whatsapp", { token, request_id: rid, base_url: BACKEND })).data;
    setWa(r);
  };
  const issue = async () => {
    setBusy("issue");
    try {
      const r = (await api.post("/gift-voucher/generate", { token, request_id: d.booking_id })).data;
      toast.success(`Voucher ${r.voucher?.voucher_number || ""} issued`); changed();
    } catch (e) { toast.error(err(e)); } finally { setBusy(""); }
  };
  const reverse = async (id) => {
    const reason = window.prompt("Reason for reversing this verified payment?");
    if (!reason) return;
    try { changed((await api.post("/booking-payments/reverse", { token, receipt_id: id, reason })).data); toast.success("Payment reversed"); }
    catch (e) { toast.error(err(e)); }
  };

  return (
    <div className="space-y-4" data-testid="booking-payment-panel">
      <div className="rounded-2xl border-[3px] border-[#d9ae52] bg-gradient-to-br from-[#fffaf0] to-[#fbf0d6] p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-xl font-bold text-[#750817]" style={{ fontFamily: "'Playfair Display', serif" }}>Payment Summary</h3>
          <PaymentStatusChip st={d} testid="bp-status" />
        </div>
        <div className="mt-2 grid sm:grid-cols-2 gap-x-6">
          <div>
            <Row l={`${d.label} Ref.`} v={<span className="font-mono">{d.ref}</span>} />
            <Row l="Centre" v={d.centre_name} />
            <Row l="Customer" v={d.customer_name || "—"} />
            {d.details.map(([k, v]) => <Row key={k} l={k} v={typeof v === "number" && !["Guests", "Days"].includes(k) ? money(v) : String(v)} />)}
          </div>
          <div className="rounded-xl bg-white/80 p-3 border border-[#ead6a6] self-start">
            <Row l={gv ? "Voucher Value" : "Total Payable"} v={<span data-testid="bp-total">{money(d.booking_total)}</span>} b />
            <Row l="Amount Already Paid" v={<span className="text-emerald-700" data-testid="bp-paid">{money(d.paid)}</span>} b />
            {d.legacy_paid > 0 && <Row l="· recorded before payment system" v={money(d.legacy_paid)} />}
            <Row l="Balance Payable" v={<span className="text-[#750817]" data-testid="bp-balance">{money(d.balance)}</span>} b />
          </div>
        </div>
      </div>

      {d.centre_closed && <p className="rounded-lg bg-red-50 border border-red-300 p-3 text-red-800" data-testid="bp-closed">This centre is closed — new payment requests are disabled.</p>}
      {d.payment.mode === "NOT_CONFIGURED" && <p className="rounded-lg bg-amber-50 border border-amber-300 p-3 text-amber-900" data-testid="bp-not-configured">{d.payment.warning}</p>}
      {d.payment.mode === "DIRECT_CENTRE_PAYMENT" && <p className="rounded-lg bg-sky-50 border border-sky-300 p-3 text-sky-900" data-testid="bp-direct">{d.payment.instruction}</p>}

      {canRequest && (
        <div className="flex flex-wrap items-end gap-2" data-testid="bp-request-row">
          <div className="w-44"><Label>Amount to request ({sym})</Label>
            <Input type="number" value={amount} disabled={gv} onChange={(e) => setAmount(e.target.value)} data-testid="bp-amount" /></div>
          {!gv && <button type="button" className="bi-pill" onClick={() => setAmount(String(d.balance))} data-testid="bp-full-balance">Full balance</button>}
          <button type="button" className="bi-pill !bg-[#750817] !text-[#fff3c4]" disabled={busy === "gen"} onClick={generate} data-testid="bp-generate">
            {busy === "gen" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}{d.payment.mode === "INDIA_UPI" ? "Generate Payment Link" : "Save Payment Request"}</button>
          {openReq && <button type="button" className="bi-pill" onClick={() => share(openReq.id).catch((e) => toast.error(err(e)))} data-testid="bp-share">
            <MessageSquare className="h-4 w-4" />{gv ? "Share Payment Link" : "Share on WhatsApp"}</button>}
          {d.payment.qr_url && <a className="bi-pill" href={`${BACKEND}${d.payment.qr_url}`} target="_blank" rel="noreferrer" data-testid="bp-view-qr"><QrCode className="h-4 w-4" />View Centre QR</a>}
        </div>
      )}
      {d.status !== "PAID" && d.status !== "CANCELLED" && !(gv && d.voucher_issued) && (
        <button type="button" className="bi-pill" onClick={() => setVerifying(!verifying)} data-testid="bp-verify-toggle"><ShieldCheck className="h-4 w-4" />Verify Payment</button>
      )}
      {verifying && <VerifyForm data={d} token={token} onDone={(r) => { setVerifying(false); changed(r); }} />}
      {gv && d.status === "PAID" && !d.voucher_issued && (
        <button type="button" className="bi-pill !bg-emerald-700 !text-white" disabled={busy === "issue"} onClick={issue} data-testid="bp-issue-voucher">
          {busy === "issue" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Gift className="h-4 w-4" />}Issue Voucher</button>
      )}
      {gv && d.voucher_issued && <p className="rounded-lg bg-emerald-50 border border-emerald-300 p-3 text-emerald-900" data-testid="bp-voucher-issued">Voucher already issued for this request.</p>}

      {wa && (
        <div className="rounded-xl border-2 border-[#ead6a6] bg-white p-3 space-y-2" data-testid="bp-wa-preview">
          <pre className="whitespace-pre-wrap text-sm font-sans">{wa.text}</pre>
          <div className="flex flex-wrap gap-2">
            <a className="bi-pill !bg-green-600 !text-white" href={wa.wa_url} target="_blank" rel="noreferrer" data-testid="bp-open-wa"><MessageSquare className="h-4 w-4" />Open WhatsApp</a>
            <button type="button" className="bi-pill" onClick={() => navigator.clipboard.writeText(wa.text).then(() => toast.success("Copied"))} data-testid="bp-copy"><Copy className="h-4 w-4" />Copy message</button>
          </div>
          <p className="text-xs text-[#5a3a22]">WhatsApp opens with the message ready — it is only sent when you press send in WhatsApp.</p>
        </div>
      )}

      <div className="space-y-2" data-testid="bp-history">
        <h4 className="text-lg font-bold text-[#750817] flex items-center gap-2"><IndianRupee className="h-5 w-5" />Payment History</h4>
        <div className="overflow-x-auto rounded-xl border border-[#ead6a6] bg-white">
          <table className="w-full text-sm">
            <thead className="bg-[#fbf0d6] text-left"><tr>{["Type", "Date", "Amount", "UTR / Ref", "Status", "Verified / Created By", "Balance at request"].map((h) => <th key={h} className="px-2 py-1.5">{h}</th>)}<th /></tr></thead>
            <tbody>
              {(d.receipts || []).map((r) => (
                <tr key={r.id} className="border-t" data-testid={`bp-receipt-${r.id}`}>
                  <td className="px-2 py-1.5">Payment · {r.method}</td><td className="px-2 py-1.5">{r.date}</td><td className="px-2 py-1.5 font-mono">{money(r.amount)}</td>
                  <td className="px-2 py-1.5 font-mono">{r.reference || "—"}</td>
                  <td className="px-2 py-1.5">{r.status === "VERIFIED" ? <span className="text-emerald-700 font-bold">Verified</span> : <span className="text-red-700 font-bold" title={r.reverse_reason}>Reversed</span>}</td>
                  <td className="px-2 py-1.5">{r.verified_by} · {(r.verified_at || "").slice(0, 10)}</td><td className="px-2 py-1.5">—</td>
                  <td className="px-2 py-1.5">{isAdmin && r.status === "VERIFIED" && !(gv && d.voucher_issued) && <button type="button" className="text-red-700" title="Reverse" onClick={() => reverse(r.id)} data-testid={`bp-reverse-${r.id}`}><Undo2 className="h-4 w-4" /></button>}</td>
                </tr>))}
              {(d.requests || []).map((r) => (
                <tr key={r.id} className="border-t text-[#5a3a22]" data-testid={`bp-request-${r.id}`}>
                  <td className="px-2 py-1.5">Request · {r.purpose}</td><td className="px-2 py-1.5">{(r.created_at || "").slice(0, 10)}</td><td className="px-2 py-1.5 font-mono">{money(r.amount_requested)}</td>
                  <td className="px-2 py-1.5">—</td><td className="px-2 py-1.5">{r.status === "SHARED" ? "Link shared" : r.status === "REQUESTED" ? "Link created" : r.status.toLowerCase()}</td>
                  <td className="px-2 py-1.5">{r.created_by}</td><td className="px-2 py-1.5 font-mono">{money(r.booking_total - r.already_paid)}</td><td />
                </tr>))}
              {!d.receipts?.length && !d.requests?.length && <tr><td colSpan={8} className="px-2 py-3 text-center text-[#5a3a22]">No payment requests yet.</td></tr>}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-[#5a3a22]">A payment link is not proof of payment — only verified payments (with UTR) count as received.</p>
      </div>
    </div>
  );
}

export function BookingPaymentDialog({ open, onClose, session, bookingType, bookingId, onChanged }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-[#fffdf8]" data-testid="bp-dialog">
        <DialogHeader><DialogTitle className="text-2xl text-[#750817]">Payment</DialogTitle></DialogHeader>
        {open && <BookingPaymentPanel session={session} bookingType={bookingType} bookingId={bookingId} onChanged={onChanged} />}
      </DialogContent>
    </Dialog>
  );
}
